/**
 * LGU Aid UI kit (docs/DESIGN.md). Accessible first: 48 px controls, 16 px inputs (no
 * iOS zoom), sentence-case labels, high contrast, visible focus. Government blue for
 * actions, sun yellow only for balances. The memorable element is the passbook.
 */
import { AlertTriangle, ArrowLeft, ChevronRight, Loader2, X } from 'lucide-react';
import { forwardRef, useEffect, useId, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { ApiError } from '../lib/api';
import { publicFileUrl } from '../lib/files';
import { useI18n } from '../lib/i18n';

export const cx = (...c: (string | false | null | undefined)[]): string => twMerge(c.filter(Boolean).join(' '));

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'sun';
const VARIANT: Record<Variant, string> = {
  primary: 'bg-brand text-brand-ink hover:brightness-110 active:brightness-95',
  secondary: 'bg-card text-ink border border-line hover:bg-paper',
  ghost: 'text-brand hover:bg-brand-soft',
  danger: 'bg-danger-soft text-danger hover:brightness-95',
  sun: 'bg-sun text-sun-ink hover:brightness-105',
};

export const Button = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; loading?: boolean; icon?: ReactNode; size?: 'md' | 'sm'; block?: boolean }>(
  function Button({ variant = 'primary', loading, icon, size = 'md', block, className, children, disabled, ...rest }, ref) {
    return (
      <button
        ref={ref}
        type={rest.type ?? 'button'}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        className={cx(
          'inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition disabled:cursor-not-allowed disabled:opacity-50',
          size === 'md' ? 'min-h-12 px-5 text-base' : 'min-h-10 px-3.5 text-sm',
          block && 'w-full',
          VARIANT[variant],
          className,
        )}
        {...rest}
      >
        {loading ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" /> : icon}
        {children}
      </button>
    );
  },
);

export function Label({ htmlFor, children, hint, optional }: { htmlFor?: string; children: ReactNode; hint?: ReactNode; optional?: boolean }) {
  const { t } = useI18n();
  return (
    <div className="mb-1.5">
      <label htmlFor={htmlFor} className="block text-sm font-semibold text-ink">
        {children} {optional && <span className="font-normal text-muted">({t('Optional').toLowerCase()})</span>}
      </label>
      {hint && <p className="mt-0.5 text-xs text-muted">{hint}</p>}
    </div>
  );
}

const inputCls = 'block w-full min-h-12 rounded-xl border border-line bg-card px-3.5 text-base text-ink placeholder:text-muted/70 outline-none transition focus:border-brand focus:ring-2 focus:ring-brand/25 disabled:opacity-60';

export function Field({ label, hint, error, optional, className, ...input }: InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: ReactNode; error?: string; optional?: boolean }) {
  const id = useId();
  return (
    <div className={className}>
      <Label htmlFor={id} hint={hint} optional={optional}>{label}</Label>
      <input id={id} aria-invalid={!!error || undefined} aria-describedby={error ? `${id}-e` : undefined} className={cx(inputCls, error && 'border-danger')} {...input} />
      {error && <p id={`${id}-e`} className="mt-1 text-sm text-danger">{error}</p>}
    </div>
  );
}

export function TextArea({ label, hint, error, optional, className, ...input }: TextareaHTMLAttributes<HTMLTextAreaElement> & { label: string; hint?: ReactNode; error?: string; optional?: boolean }) {
  const id = useId();
  return (
    <div className={className}>
      <Label htmlFor={id} hint={hint} optional={optional}>{label}</Label>
      <textarea id={id} rows={4} aria-invalid={!!error || undefined} className={cx(inputCls, 'py-3 leading-relaxed', error && 'border-danger')} {...input} />
      {error && <p className="mt-1 text-sm text-danger">{error}</p>}
    </div>
  );
}

export function SelectField({ label, hint, error, optional, className, children, ...input }: SelectHTMLAttributes<HTMLSelectElement> & { label: string; hint?: ReactNode; error?: string; optional?: boolean }) {
  const id = useId();
  return (
    <div className={className}>
      <Label htmlFor={id} hint={hint} optional={optional}>{label}</Label>
      <select id={id} className={cx(inputCls, 'appearance-none bg-[length:1rem] pr-9', error && 'border-danger')} {...input}>{children}</select>
      {error && <p className="mt-1 text-sm text-danger">{error}</p>}
    </div>
  );
}

export function Card({ children, className, as: As = 'section' }: { children: ReactNode; className?: string; as?: 'section' | 'div' | 'article' }) {
  return <As className={cx('rounded-ticket border border-line bg-card p-4', className)}>{children}</As>;
}

type Tone = 'neutral' | 'brand' | 'sun' | 'danger' | 'info';
const TONE: Record<Tone, string> = {
  neutral: 'bg-paper text-muted border border-line',
  brand: 'bg-brand-soft text-brand',
  sun: 'bg-sun-soft text-ink',
  danger: 'bg-danger-soft text-danger',
  info: 'bg-info-soft text-info',
};
export function Chip({ tone = 'neutral', children, icon, className }: { tone?: Tone; children: ReactNode; icon?: ReactNode; className?: string }) {
  return <span className={cx('inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold', TONE[tone], className)}>{icon}{children}</span>;
}

export function Avatar({ name, fileId, size = 40 }: { name: string; fileId?: string | null; size?: number }) {
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('') || '?';
  const url = publicFileUrl(fileId);
  return url ? (
    <img src={url} alt="" width={size} height={size} className="shrink-0 rounded-full object-cover" style={{ width: size, height: size }} />
  ) : (
    <span aria-hidden className="display grid shrink-0 place-items-center rounded-full bg-brand-soft text-brand" style={{ width: size, height: size, fontSize: size * 0.38 }}>{initials}</span>
  );
}

export function Spinner({ label }: { label?: string }) {
  const { t } = useI18n();
  return (
    <div role="status" className="flex items-center justify-center gap-2 py-10 text-muted">
      <Loader2 aria-hidden className="h-5 w-5 animate-spin" />
      <span className="text-sm">{label ?? t('Loading…')}</span>
    </div>
  );
}

export const errorText = (e: unknown): string => (e instanceof ApiError ? e.fieldMessages.join(' ') || e.message : e instanceof Error ? e.message : 'Something went wrong. Try again.');

export function ErrorNote({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t } = useI18n();
  if (!error) return null;
  return (
    <div role="alert" className="flex items-start gap-2 rounded-xl bg-danger-soft p-3 text-sm text-danger">
      <AlertTriangle aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
      <span className="flex-1">{errorText(error)}</span>
      {onRetry && <button type="button" onClick={onRetry} className="font-semibold underline">{t('Retry')}</button>}
    </div>
  );
}

export function Note({ tone = 'info', children, icon }: { tone?: Tone; children: ReactNode; icon?: ReactNode }) {
  return <div className={cx('flex items-start gap-2 rounded-xl p-3 text-sm', TONE[tone])}>{icon}<div className="flex-1">{children}</div></div>;
}

export function Empty({ icon, title, hint, action }: { icon?: ReactNode; title: string; hint?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
      {icon && <div className="mb-1 text-muted">{icon}</div>}
      <p className="text-base font-semibold">{title}</p>
      {hint && <p className="max-w-sm text-sm text-muted">{hint}</p>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

/** Screen header: back arrow, title, optional action. Sticky on phones. */
export function TopBar({ title, back, action, sub }: { title: ReactNode; back?: string | boolean; action?: ReactNode; sub?: ReactNode }) {
  const navigate = useNavigate();
  const { t } = useI18n();
  return (
    <header className="sticky top-0 z-20 flex min-h-14 items-center gap-2 border-b border-line bg-paper/95 px-2 backdrop-blur">
      {back ? (
        <button type="button" onClick={() => (typeof back === 'string' ? navigate(back) : navigate(-1))} className="grid h-11 w-11 place-items-center rounded-full hover:bg-card" aria-label={t('Back')}>
          <ArrowLeft aria-hidden className="h-5 w-5" />
        </button>
      ) : <span className="w-2" />}
      <div className="min-w-0 flex-1">
        <h1 className="truncate text-lg font-bold">{title}</h1>
        {sub && <p className="truncate text-xs text-muted">{sub}</p>}
      </div>
      {action}
    </header>
  );
}

/** Bottom sheet on phones, centred dialog on wider screens. */
export function Sheet({ open, title, onClose, children, footer }: { open: boolean; title: string; onClose: () => void; children: ReactNode; footer?: ReactNode }) {
  const { t } = useI18n();
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true" aria-label={title}>
      <button type="button" aria-label={t('Close')} className="absolute inset-0 bg-ink/45" onClick={onClose} />
      <div className="relative flex max-h-[92vh] w-full max-w-lg flex-col rounded-t-3xl bg-paper shadow-2xl sm:rounded-3xl">
        <div className="flex items-center gap-2 border-b border-line px-5 py-3.5">
          <h2 className="flex-1 text-lg font-bold">{title}</h2>
          <button type="button" onClick={onClose} className="grid h-10 w-10 place-items-center rounded-full hover:bg-card" aria-label={t('Close')}><X aria-hidden className="h-5 w-5" /></button>
        </div>
        <div className="overflow-y-auto px-5 py-4">{children}</div>
        {footer && <div className="safe-bottom border-t border-line px-5 py-3">{footer}</div>}
      </div>
    </div>
  );
}

export function Confirm({ open, title, body, confirmLabel, danger, loading, onConfirm, onClose, children }: { open: boolean; title: string; body?: ReactNode; confirmLabel: string; danger?: boolean; loading?: boolean; onConfirm: () => void; onClose: () => void; children?: ReactNode }) {
  const { t } = useI18n();
  return (
    <Sheet open={open} title={title} onClose={onClose} footer={
      <div className="flex gap-2">
        <Button variant="secondary" className="flex-1" onClick={onClose}>{t('Cancel')}</Button>
        <Button variant={danger ? 'danger' : 'primary'} className="flex-1" loading={loading} onClick={onConfirm}>{confirmLabel}</Button>
      </div>
    }>
      {body && <div className="text-sm text-muted">{body}</div>}
      {children}
    </Sheet>
  );
}

export function Segmented<V extends string>({ value, options, onChange, label }: { value: V; options: { value: V; label: string }[]; onChange: (v: V) => void; label: string }) {
  return (
    <div role="tablist" aria-label={label} className="flex gap-1 overflow-x-auto rounded-xl bg-card p-1 ring-1 ring-line">
      {options.map((o) => (
        <button key={o.value} role="tab" type="button" aria-selected={o.value === value} onClick={() => onChange(o.value)}
          className={cx('min-h-10 flex-1 whitespace-nowrap rounded-lg px-3 text-sm font-semibold transition', o.value === value ? 'bg-brand text-brand-ink' : 'text-muted hover:text-ink')}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Toggle({ checked, onChange, label, hint }: { checked: boolean; onChange: (v: boolean) => void; label: string; hint?: string }) {
  return (
    <label className="flex min-h-12 cursor-pointer items-center gap-3">
      <span className="flex-1">
        <span className="block font-medium">{label}</span>
        {hint && <span className="block text-xs text-muted">{hint}</span>}
      </span>
      <span className="relative">
        <input type="checkbox" className="peer sr-only" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <span className="block h-7 w-12 rounded-full bg-line transition peer-checked:bg-brand peer-focus-visible:ring-2 peer-focus-visible:ring-info" />
        <span className="absolute left-1 top-1 h-5 w-5 rounded-full bg-white shadow transition peer-checked:translate-x-5" />
      </span>
    </label>
  );
}

/** A tappable row for lists and menus. */
export function Row({ title, sub, left, right, onClick, href }: { title: ReactNode; sub?: ReactNode; left?: ReactNode; right?: ReactNode; onClick?: () => void; href?: string }) {
  const navigate = useNavigate();
  const go = onClick ?? (href ? () => navigate(href) : undefined);
  const inner = (
    <>
      {left}
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium">{title}</span>
        {sub && <span className="block truncate text-sm text-muted">{sub}</span>}
      </span>
      {right}
      {go && <ChevronRight aria-hidden className="h-4 w-4 shrink-0 text-muted" />}
    </>
  );
  return go ? (
    <button type="button" onClick={go} className="flex min-h-14 w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-paper">{inner}</button>
  ) : <div className="flex min-h-14 items-center gap-3 px-4 py-2.5">{inner}</div>;
}

export function List({ children }: { children: ReactNode }) {
  return <div className="divide-y divide-line overflow-hidden rounded-ticket border border-line bg-card">{children}</div>;
}

/** Section heading: sentence case, no eyebrow labels. */
export function H2({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-2 mt-6 flex items-end justify-between gap-2">
      <h2 className="text-base font-bold">{children}</h2>
      {action}
    </div>
  );
}

export function Page({ children, className }: { children: ReactNode; className?: string }) {
  return <main className={cx('mx-auto w-full max-w-xl px-4 pb-28 pt-3', className)}>{children}</main>;
}

/**
 * The passbook (DESIGN.md): ruled rows, a double margin rule, tabular money. Used for
 * statements, wallet balances and billing lines.
 */
export function Passbook({ children, className, label }: { children: ReactNode; className?: string; label?: string }) {
  return <div role="table" aria-label={label} className={cx('passbook rounded-ticket border border-line bg-card pl-3', className)}>{children}</div>;
}

export function PassbookRow({ left, middle, right, sub, tone }: { left: ReactNode; middle?: ReactNode; right: ReactNode; sub?: ReactNode; tone?: 'credit' | 'debit' }) {
  return (
    <div role="row" className="passbook-row gap-3 px-3 py-1.5">
      <span role="cell" className="w-24 shrink-0 font-mono text-xs text-muted">{left}</span>
      <span role="cell" className="min-w-0 flex-1">
        <span className="block truncate">{middle}</span>
        {sub && <span className="block truncate text-xs text-muted">{sub}</span>}
      </span>
      <span role="cell" className={cx('tabular shrink-0 text-right font-mono', tone === 'credit' && 'text-info', tone === 'debit' && 'text-ink')}>{right}</span>
    </div>
  );
}

/** A balance in big type with the sun highlight, e.g. on the POS and the wallet. */
export function Balance({ label, value, sub }: { label: string; value: string; sub?: ReactNode }) {
  return (
    <div className="rounded-ticket border border-sun/50 bg-sun-soft px-4 py-3">
      <p className="text-sm text-muted">{label}</p>
      <p className="display tabular text-3xl">{value}</p>
      {sub && <p className="text-xs text-muted">{sub}</p>}
    </div>
  );
}
